import { Badge } from '@aecom-ai-engineering/react-components';
import { type FC, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AGENT_MARK_FALLBACK, AGENT_MARKS } from '@/components/agent-marks';

import { agentSlug } from './agent-details-content';
import { type DirectoryAgent } from './filter-agents';

interface AgentsDirectoryCardProps {
  agent: DirectoryAgent;
  /** Opens the agent's details. `trigger` is where focus returns on close. */
  onOpen: (agent: DirectoryAgent, trigger: HTMLElement) => void;
}

/**
 * One agent in the directory's grid. Figma 11233:59912, hover 11233:59918.
 *
 * The whole card opens the agent's details dialog. It is a real <button>
 * stretched over the article, as project-overview/agent-card.tsx does, so
 * focus, Enter and Space come free.
 *
 * That is also why this is app-local rather than a design-system `AgentCard`
 * beside `ProjectCard`: the taxonomy it renders is still settling. Promoting it
 * is a follow-up, once the categories and tags have been through product.
 */
export const AgentsDirectoryCard: FC<AgentsDirectoryCardProps> = ({ agent, onOpen }) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = () => {
    if (buttonRef.current !== null) onOpen(agent, buttonRef.current);
  };

  // An <article> with no accessible name is announced as just "article", and
  // the grid holds 27 of them. Naming each from its own heading is invisible on
  // screen and is what makes them distinguishable when moving between them.
  const titleId = useId();
  // The button is where keyboard focus lands, and its label names the agent
  // only. Describing it by the card copy reads the description out as well.
  const descriptionId = useId();

  return (
    // 272 is a floor, not a fixed height, so a row can grow if it ever has to.
    // In practice nothing makes it: the description clamps to what the floor
    // leaves, which is why every card comes out the same height and the grid
    // stays even. The grid stretches its items, so this is also what stops a
    // short card collapsing beside a tall one.
    //
    // `justify-between` is what the frame does with the slack — the logo and
    // copy sit at the top, the tags on the floor, and a short description
    // widens the gap between them rather than floating the tags up.
    <article
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      className="group relative isolate flex min-h-[272px] cursor-pointer flex-col justify-between rounded-2xl border border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-6 transition-colors duration-[120ms] ease-[ease] hover:border-[var(--ds-color-border-secondary)]"
      data-agent-slug={agentSlug(agent.serviceName)}
    >
      {/* Verbatim from project-overview/agent-card.tsx, so the two agent cards
          answer a pointer the same way. The frame's `brand-gradient`:
          brand-light at the top falling to nothing at the bottom.

          Its own element rather than a background on the card, because
          `background-image` does not interpolate — a gradient swapped in on
          hover would appear at once where this fades. `isolate` on the article
          above puts it in a stacking context where -z-10 paints it over the
          card's own fill but under every piece of content; without that the
          negative index would drop it behind the fill entirely.

          120ms ease is the design system's own interaction timing, from
          Button.css, and the border now shares it rather than snapping. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-gradient-to-b from-[var(--ds-color-bg-brand-light)] to-transparent opacity-0 transition-opacity duration-[120ms] ease-[ease] group-hover:opacity-100"
      />

      {/* Verbatim from project-overview/agent-card.tsx. SVG source strings, so
          dangerouslySetInnerHTML is the only way in — an <img src> would drop the
          .ds-agent-logo classes. data-ds-agent-logo-inline is load-bearing: it is
          the only rule in Logos.css supplying --ds-color-text-invert, and the
          glyphs are currentColor, so without it they render dark on the dark-green
          tile. The marquee's contract test pins that rule.

          Every directory agent has a mark of its own; the fallback covers
          BID_REVIEW, which is off the list. */}
      <div className="flex min-h-0 flex-col gap-4 pb-[14px]">
        <span
          aria-hidden
          data-ds-agent-logo-inline
          dangerouslySetInnerHTML={{
            __html: AGENT_MARKS[agent.serviceName] ?? AGENT_MARK_FALLBACK,
          }}
        />

        <div className="flex min-h-0 flex-col gap-2">
          {/* Not truncated, unlike the project card's: nothing here is a control
              with an aria-label to recover a cut name from, so a long title wraps.
              A one-word compound such as nn "Komponentreferansekontroll" has no
              space to wrap at: hyphens-auto splits it by <html lang>, and
              wrap-break-word splits it anyway in a language with no dictionary. */}
          <h3
            className="wrap-break-word hyphens-auto text-[18px] leading-[22px] font-medium text-[var(--ds-color-text-primary)]"
            id={titleId}
          >
            {agent.title}
          </h3>
          {/* raw-stone-600, the same choice agent-card.tsx made and for the same
              reason: the frame's text-secondary is #57534E, while the exported
              --ds-color-text-secondary is #78716C. Drift for the token re-export
              to settle. */}
          {/* Five lines, which is what the 272 floor leaves once everything
              around it is counted: 272 less 2 of border and 48 of padding is
              222, less the 40 logo, the 16 and 8 gaps, the 22 title, the 14 of
              padding under the copy and the 20 tag row — 102, and 102 over the
              18 line-height is five whole lines. The padding sits on the wrapper,
              not here, because padding on a clamped box shows the cut line.
              Each extra row of tags adds to the card rather than taking a line.
              Clamped rather than clipped so the cut carries an ellipsis, and
              clamped to the space rather than to three lines so a long
              description uses what the card has. Recompute it if the floor or
              anything above or below it changes height. */}
          <p
            className="line-clamp-5 text-[13px] leading-[18px] text-[var(--ds-color-raw-stone-600)]"
            id={descriptionId}
          >
            {agent.description}
          </p>
        </div>
      </div>

      {/* Wraps onto as many rows as the tags need, so every tag shows in full.
          A card with a long taxonomy grows past the 272 floor, and the grid
          stretches the rest of its row to match.

          A div, not a ul: Badge renders a span, so a list would need
          wrapping li elements, and "list, 3 items" announced on every card
          is noise rather than structure.

          Under the card's button, so a click on a tag opens the details. */}
      <div className="flex flex-wrap gap-x-1 gap-y-2">
        {agent.tags.map((tag) => (
          <Badge isRound key={tag.key} label={tag.label} variant="outline" />
        ))}
      </div>

      <button
        aria-describedby={descriptionId}
        aria-haspopup="dialog"
        aria-label={t('agents-directory.details.open', { name: agent.title })}
        className="absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-color-border-secondary)]"
        onClick={open}
        ref={buttonRef}
        type="button"
      />
    </article>
  );
};
